export const INVENT_AUTH_VERSION = 1 as const
export const INVENT_AUTH_REQUEST_TYPE = "legacy-keeper:invent-auth:request" as const
export const INVENT_AUTH_RESPONSE_TYPE = "legacy-keeper:invent-auth:token" as const
export const INVENT_AUTH_ERROR_TYPE = "legacy-keeper:invent-auth:error" as const
export const INVENT_GUIDED_FORM_VERSION = 1 as const
export const INVENT_GUIDED_FORM_READY_TYPE = "legacy-keeper:guided-form:ready" as const
export const INVENT_GUIDED_FORM_NO_DATA_TYPE = "legacy-keeper:guided-form:no-data" as const
export const INVENT_GUIDED_FORM_ERROR_TYPE = "legacy-keeper:guided-form:error" as const

const DEFAULT_TIMEOUT_MS = 5_000
const MAX_TIMEOUT_MS = 10_000
const TOKEN_CACHE_MS = 10_000

let cachedToken: { origin: string; token: string; expiresAt: number } | undefined
let tokenRequest: { origin: string; promise: Promise<string> } | undefined

interface RequestInventIframeTokenOptions {
  parentOrigin: string
  timeoutMs?: number
  currentWindow?: Window
  createRequestId?: () => string
}

export type InventGuidedFormLifecycleResult = "ready" | "no-data" | "error"

interface PostInventGuidedFormLifecycleOptions {
  result: InventGuidedFormLifecycleResult
  parentOrigin: string
  currentWindow?: Window
}

const guidedFormLifecycleTypes = {
  ready: INVENT_GUIDED_FORM_READY_TYPE,
  "no-data": INVENT_GUIDED_FORM_NO_DATA_TYPE,
  error: INVENT_GUIDED_FORM_ERROR_TYPE,
} as const

export function parseExactHttpsOrigin(value: string | undefined): string | undefined {
  if (value === undefined) return undefined
  const candidate = value.trim()
  if (!candidate) return undefined

  try {
    const parsed = new URL(candidate)
    if (parsed.protocol !== "https:" || parsed.origin !== candidate) return undefined
    return parsed.origin
  } catch {
    return undefined
  }
}

function hasExactKeys(value: Record<string, unknown>, expectedKeys: readonly string[]): boolean {
  const keys = Object.keys(value)
  return keys.length === expectedKeys.length && expectedKeys.every((key) => keys.includes(key))
}

function isTokenResponse(
  value: unknown,
  requestId: string,
): value is { type: typeof INVENT_AUTH_RESPONSE_TYPE; token: string } {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const record = value as Record<string, unknown>

  return (
    hasExactKeys(record, ["type", "version", "requestId", "token"]) &&
    record.type === INVENT_AUTH_RESPONSE_TYPE &&
    record.version === INVENT_AUTH_VERSION &&
    record.requestId === requestId &&
    typeof record.token === "string" &&
    record.token.length > 0
  )
}

function isErrorResponse(value: unknown, requestId: string): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const record = value as Record<string, unknown>

  return (
    hasExactKeys(record, ["type", "version", "requestId"]) &&
    record.type === INVENT_AUTH_ERROR_TYPE &&
    record.version === INVENT_AUTH_VERSION &&
    record.requestId === requestId
  )
}

function getTimeoutMs(timeoutMs: number | undefined): number {
  if (timeoutMs === undefined) return DEFAULT_TIMEOUT_MS
  if (!Number.isFinite(timeoutMs)) return DEFAULT_TIMEOUT_MS
  return Math.min(Math.max(Math.floor(timeoutMs), 1), MAX_TIMEOUT_MS)
}

export function isInventEmbedPathname(pathname: string): boolean {
  return pathname.startsWith("/integrations/invent/embed/")
}

export function clearInventIframeToken(): void {
  cachedToken = undefined
  tokenRequest = undefined
}

export function postInventGuidedFormLifecycle({
  result,
  parentOrigin,
  currentWindow = window,
}: PostInventGuidedFormLifecycleOptions): boolean {
  const exactParentOrigin = parseExactHttpsOrigin(parentOrigin)
  if (!exactParentOrigin || currentWindow.parent === currentWindow) return false

  currentWindow.parent.postMessage(
    {
      type: guidedFormLifecycleTypes[result],
      version: INVENT_GUIDED_FORM_VERSION,
    },
    exactParentOrigin,
  )
  return true
}

export function getInventIframeToken(options: RequestInventIframeTokenOptions): Promise<string> {
  const now = Date.now()
  if (cachedToken?.origin === options.parentOrigin && cachedToken.expiresAt > now) {
    return Promise.resolve(cachedToken.token)
  }

  if (tokenRequest?.origin === options.parentOrigin) return tokenRequest.promise

  const promise = requestInventIframeToken(options)
    .then((token) => {
      cachedToken = {
        origin: options.parentOrigin,
        token,
        expiresAt: Date.now() + TOKEN_CACHE_MS,
      }
      return token
    })
    .finally(() => {
      if (tokenRequest?.promise === promise) tokenRequest = undefined
    })

  tokenRequest = { origin: options.parentOrigin, promise }
  return promise
}

export function requestInventIframeToken({
  parentOrigin,
  timeoutMs,
  currentWindow = window,
  createRequestId = () => crypto.randomUUID(),
}: RequestInventIframeTokenOptions): Promise<string> {
  const exactParentOrigin = parseExactHttpsOrigin(parentOrigin)
  if (!exactParentOrigin) {
    return Promise.reject(new Error("INVENT portal origin is not configured"))
  }

  const parentWindow = currentWindow.parent
  if (parentWindow === currentWindow) {
    return Promise.reject(new Error("INVENT authentication requires an embedded window"))
  }

  const requestId = createRequestId()

  return new Promise<string>((resolve, reject) => {
    const finish = (callback: () => void) => {
      globalThis.clearTimeout(timeout)
      currentWindow.removeEventListener("message", handleMessage)
      callback()
    }
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== exactParentOrigin || event.source !== parentWindow) return

      if (isTokenResponse(event.data, requestId)) {
        finish(() => resolve(event.data.token))
        return
      }

      if (isErrorResponse(event.data, requestId)) {
        finish(() => reject(new Error("INVENT authentication was denied")))
      }
    }
    const timeout = globalThis.setTimeout(() => {
      finish(() => reject(new Error("Timed out while requesting INVENT authentication")))
    }, getTimeoutMs(timeoutMs))

    currentWindow.addEventListener("message", handleMessage)
    parentWindow.postMessage(
      {
        type: INVENT_AUTH_REQUEST_TYPE,
        version: INVENT_AUTH_VERSION,
        requestId,
      },
      exactParentOrigin,
    )
  })
}
